import { getFirstPageId, parsePageLogic, evaluatePageLogic, resolveNextPageId } from "./evaluatePageLogic";
import { findHiddenInputIds, isInputShown } from "./inputVisibility";
import type { FormAnswerMap, PageLogicPageRef, PageLogicResolution } from "./types";

/** A page with the inputs it renders, so answers can be tied back to the page that asked them. */
export interface PageLogicPageWithInputs extends PageLogicPageRef {
    inputs?: ReadonlyArray<{ input_id?: string | null; show_when?: unknown; options_from?: unknown }> | null;
}

function pageInputIds(page: PageLogicPageWithInputs): string[] {
    return (page.inputs ?? []).flatMap((input) => (input.input_id ? [input.input_id] : []));
}

/**
 * Only answers the patient could have given on the path so far. An answer to an input on a page
 * that has not been reached is left out, so a stale answer from an abandoned branch cannot steer
 * a later rule. Answers to inputs that sit on no page of this form pass through untouched.
 */
function answersOnPath(
    answers: FormAnswerMap,
    inputIdsByPage: ReadonlyMap<string, string[]>,
    visited: ReadonlySet<string>
): FormAnswerMap {
    const unreached = new Set(
        [...inputIdsByPage].flatMap(([pageId, inputIds]) => (visited.has(pageId) ? [] : inputIds))
    );
    return Object.fromEntries(Object.entries(answers).filter(([inputId]) => !unreached.has(inputId)));
}

/**
 * The pages a patient reaches with these answers, in order, by replaying the page logic from the
 * first page. A page reached twice (a jump back to an earlier page) stops the walk.
 */
export function resolveVisitedPageIds(pages: PageLogicPageWithInputs[], answers: FormAnswerMap): string[] {
    const inputIdsByPage = new Map(pages.map((page) => [page.page_id, pageInputIds(page)]));
    const path: string[] = [];
    const visited = new Set<string>();

    let currentId = getFirstPageId(pages);
    while (currentId && !visited.has(currentId)) {
        path.push(currentId);
        visited.add(currentId);
        const pageId = currentId;
        const current = pages.find((page) => page.page_id === pageId);
        const resolution = evaluatePageLogic(
            parsePageLogic(current?.page_logic),
            answersOnPath(answers, inputIdsByPage, visited),
            pages,
            pageId
        );
        currentId = resolution.kind === "page" ? resolution.page_id : null;
    }

    return path;
}

/**
 * Inputs the patient was not asked: those on pages they skipped, and those hidden on a visited page by
 * their show_when condition. Their answers must not be saved or exported, even when the patient filled
 * them in before going back or changing the answer that showed them.
 */
export function findSkippedInputIds(pages: PageLogicPageWithInputs[], answers: FormAnswerMap): string[] {
    const visited = new Set(resolveVisitedPageIds(pages, answers));
    const onSkippedPages = pages.filter((page) => !visited.has(page.page_id)).flatMap(pageInputIds);
    const skipped = new Set(onSkippedPages);
    const askedAnswers = Object.fromEntries(Object.entries(answers).filter(([id]) => !skipped.has(id)));
    const visitedInputs = pages.filter((page) => visited.has(page.page_id)).flatMap((page) => page.inputs ?? []);
    return [...onSkippedPages, ...findHiddenInputIds(visitedInputs, askedAnswers)];
}

/** True when the page has questions and every one of them is hidden for these answers. */
export function isPageFullyHidden(page: PageLogicPageWithInputs, answers: FormAnswerMap): boolean {
    const inputs = (page.inputs ?? []).filter((input) => input.input_id);
    if (inputs.length === 0) return false;
    const hidden = new Set(findHiddenInputIds(inputs, answers));
    return inputs.every((input) => hidden.has(input.input_id!));
}

/**
 * The next page the patient is actually asked anything on: the page logic's next page, skipping any page
 * whose questions are all hidden (e.g. a causes page when no condition was ticked) by following that
 * page's own logic. Pages without questions (text only) are never skipped. A loop stops at the page it
 * would revisit.
 */
export function resolveNextAskedPageId(
    currentPageId: string,
    pages: PageLogicPageWithInputs[],
    answers: FormAnswerMap
): PageLogicResolution {
    const seen = new Set<string>([currentPageId]);
    let resolution = resolveNextPageId(currentPageId, pages, answers);
    while (resolution.kind === "page" && !seen.has(resolution.page_id)) {
        // Copied out: the callback below does not keep the narrowing of the reassigned \`resolution\`.
        const nextPageId = resolution.page_id;
        const page = pages.find((candidate) => candidate.page_id === nextPageId);
        if (!page || !isPageFullyHidden(page, answers)) return resolution;
        seen.add(page.page_id);
        resolution = resolveNextPageId(page.page_id, pages, answers);
    }
    return resolution;
}

/**
 * Editing one page of a finished entry: the first page after `currentPageId` that the answers reach and that
 * still has a shown question nobody has answered (a branch the edit just opened). Pages already answered are
 * walked past, not asked again. Null when the edit opens nothing new, so the edit can save and return.
 */
export function findNextUnansweredPageId(
    currentPageId: string,
    pages: PageLogicPageWithInputs[],
    answers: FormAnswerMap
): string | null {
    const seen = new Set<string>([currentPageId]);
    let fromPageId = currentPageId;
    for (;;) {
        const resolution = resolveNextAskedPageId(fromPageId, pages, answers);
        if (resolution.kind === "end" || seen.has(resolution.page_id)) return null;
        const page = pages.find((candidate) => candidate.page_id === resolution.page_id);
        if (!page) return null;
        const inputs = (page.inputs ?? []).filter((input) => input.input_id && isInputShown(input, answers));
        const hasUnanswered = inputs.some((input) => {
            const answer = answers[input.input_id!];
            return answer == null || answer === "" || (Array.isArray(answer) && answer.length === 0);
        });
        if (hasUnanswered) return page.page_id;
        seen.add(page.page_id);
        fromPageId = page.page_id;
    }
}
